/*
 * label-placement.ts - place point labels across partial-image renders
 */

import * as constants from '../constants';
import type Renderer from './renderer';


/**
 * Point label placement for a caller whose canvas shows, one at a time,
 * parts of one larger image. The map places labels by what the canvas
 * shows, so parts that overlap or abut get different decisions about a
 * label they share. The caller has the labels decided once for the
 * whole image, in three steps:
 *
 * 1. Under `Renderer.collectLabels` the map draws nothing; each frame
 *    collects the point labels the part shows, with their position in
 *    the whole image. What is collected of a label is read while the
 *    canvas shows it, where the terrain height under it and the depth
 *    hitmap are right.
 * 2. `Renderer.placeLabels` places the collected labels of all parts
 *    over the whole image, once. The caller passes a label that several
 *    parts collected from one of them.
 * 3. Under `Renderer.drawPlacedLabels` the map draws the placed labels
 *    the canvas shows and no other point label.
 *
 * The renderer does not know how the parts divide the image. The caller
 * states where a part lies in it.
 *
 * A label is identified across parts, maps and reloads of its vector
 * tile by the key `GpuGroup.draw` gives its job.
 *
 * Placement covers labels and icons that avoid overlap, without a label
 * budget or under the `scr-count7` one. Line labels and the other reduce
 * modes are placed as without it.
 */
class LabelPlacement {

    /** Retain accepted draws until geometry at their draw level is done. */
    defer(draw: () => void): void {

        this.draws_.push(draw);
    }

    /** Draw accepted labels at the normal deferred-label stage. */
    flush(): void {

        for (const draw of this.draws_) draw();
        this.draws_.length = 0;
    }

    /** A placement that collects the labels of a part of the image. */
    static collect(part: LabelPlacement.Part): LabelPlacement {

        return new LabelPlacement(part, null);
    }

    /**
     * Replay accepted labels at their recorded sheet positions.
     *
     * @param size width and height of the canvas in CSS pixels
     */
    static draw(labels: readonly LabelPlacement.Label[],
        origin: readonly [number, number],
        size: readonly [number, number]): LabelPlacement {

        const placement = new LabelPlacement(null,
            new Map(labels.map(label => [label.key, label])), origin);

        const [left, top] = origin;
        const [right, bottom] = [left + size[0], top + size[1]];

        // the vector tiles, and their ancestors, that carry a label
        // reaching into the canvas; the stick hangs below the rectangle
        for (const label of labels) {

            const [x, y] = label.position;
            const rectangle = label.rectangle;

            if (x + rectangle[2] < left || x + rectangle[0] > right
                    || y + rectangle[3] + label.stickShift < top
                    || y + rectangle[1] > bottom)
                continue;

            const match = TileKey.exec(label.key);

            // a label of a layer without tiles has no tile id
            if (!match) continue;

            const [, layer, lodText, xText, yText] = match;
            const lod = Number(lodText);

            for (let level = lod; level >= 0; level--) {

                const shift = lod - level;

                placement.sourceTiles_!.add(layer + '/' + level + '-'
                    + (Number(xText) >> shift) + '-'
                    + (Number(yText) >> shift));
            }
        }

        return placement;
    }

    /**
     * Whether the vector tile carries a placed label that reaches into
     * the canvas, or is an ancestor of one that does. Such a tile is
     * drawn even when its bounding box is outside the view, so the label
     * is not cut at the canvas edge.
     *
     * @param layer the `id` of the tile's layer, as in the label key
     * @param id the tile's lod, x and y
     */
    carriesPlaced(layer: string | null, id: readonly number[]): boolean {

        return !!this.sourceTiles_
            && this.sourceTiles_.has(layer + '/' + id.join('-'));
    }

    /** The accepted geometry, measured in the part that owns its anchor. */
    placed(key: string): LabelPlacement.Label | undefined {

        return this.placed_?.get(key);
    }

    /**
     * Places collected labels over the whole image, as a canvas of that
     * size would place them.
     *
     * @param imageSize width and height of the whole image in CSS pixels
     * @returns accepted labels with their geometry in image coordinates
     */
    static place(
        renderer: Renderer,
        labels: readonly LabelPlacement.Label[],
        imageSize: readonly [number, number],
    ): LabelPlacement.Label[] {

        const rmap = renderer.rmap;
        const config = renderer.config;
        const installed = renderer.labelPlacement;
        const placed: LabelPlacement.Label[] = [];

        // the collision grid spans the whole image while this runs
        renderer.labelPlacement = new LabelPlacement(
            { imageSize, origin: [0, 0] }, null);

        // draw levels place independently, in their order
        const levels = [...new Set(labels.map((label) => label.zIndex))];

        levels.sort((first, second) => first - second);

        for (const level of levels) {

            rmap.clear();

            const add = (label: LabelPlacement.Label, any: boolean) => {

                const [x, y] = label.position;
                const [left, top, right, bottom] = label.rectangle;
                const subjob = [label, label.stickShift];

                if (rmap.addRectangle(x + left, y + top, x + right,
                        y + bottom, label.priority, subjob, any))
                    return true;

                // a displaced label gets a second chance
                if (!any) rmap.storeRemovedRectangle(x + left, y + top,
                    x + right, y + bottom, label.priority, subjob);

                return false;
            };

            const onLevel = labels
                .filter((label) => label.zIndex === level)
                .sort((first, second) => first.key < second.key ? -1 : 1);

            // labels without a budget displace weaker ones
            for (const label of onLevel)
                if (label.importance === null) add(label, false);

            // budgeted labels take free space, the most important first
            const budgeted = onLevel.filter((label) =>
                label.importance !== null && !label.occluded);

            budgeted.sort(
                (first, second) => second.importance! - first.importance!);

            const inch = config.rendererCssDpi;

            const budget = Math.ceil(
                (imageSize[0] / inch) * (imageSize[1] / inch)
                    * config.mapFeaturesReduceParams[1]);

            let count = 0;

            for (const label of budgeted) {

                if (count >= budget) break;
                if (add(label, true)) count++;
            }

            rmap.readdRemoved();

            for (let index = 0; index < rmap.rectanglesCount; index += 6) {

                const subjob = rmap.rectangles[index + 5];
                if (subjob) placed.push(subjob[0]);
            }
        } // for each level

        renderer.labelPlacement = installed;
        rmap.clear();
        return placed;
    }

    /** Whether placement covers the job. */
    static covers(job: LabelPlacement.Job): boolean {

        if (!job.noOverlap || job.type === constants.JOB_LINE_LABEL)
            return false;

        // of the reduce modes that budget labels by the canvas, only
        // scr-count7
        const mode = job.reduce ? job.reduce[0] : 0;
        return mode === ScrCount7 || !(mode >= 7 && mode <= 11);
    }

    /**
     * Where the part the canvas shows lies in the whole image, while
     * labels are collected or placed; null while placed labels are
     * drawn.
     */
    readonly part: LabelPlacement.Part | null;

    /** The labels of the last frame drawn; null unless collecting. */
    collected: LabelPlacement.Label[] | null;

    /**
     * Whether the job is drawn now. Called from the label draw path for
     * every job placement covers; while collecting, it collects the job
     * and draws nothing.
     *
     * @param job the label or icon job
     * @param position the job's anchor in apparent pixels of the canvas,
     *   with its depth in normalized device coordinates
     * @param stickShift the height of the label's stick in pixels
     */
    admit(
        renderer: Renderer,
        job: LabelPlacement.Job,
        position: ArrayLike<number>,
        stickShift: number,
    ): boolean {

        if (this.placed_) return this.placed_.has(job.placementKey);

        const rmap = renderer.rmap;
        const [left, top, right, bottom, mode, value] = job.noOverlap;
        const [x, y, depth] = [position[0], position[1], position[2]];

        if (depth < 0 || depth > 1) return false;

        // the anchor and the rectangle lie within the image, clear of
        // its label-free margins
        if (!rmap.checkRectangle(x, y, x, y, 0)) return false;

        if (!rmap.checkRectangle(
                x + left, y + top, x + right, y + bottom, stickShift))
            return false;

        const eye = renderer.mapCamera.geometry.eye;
        const center = job.center2;

        const distance = Math.hypot(center[0] - eye[0],
            center[1] - eye[1], center[2] - eye[2]) + 0.0001;

        const config = renderer.config;
        const reduce = budgeted(job) ? job.reduce : null;

        // the rank of a label without a budget among those it overlaps
        let priority = depth;

        if (!reduce && mode === constants.NO_OVERLAP_DIRECT) priority = value;

        if (!reduce && mode !== null && mode !== constants.NO_OVERLAP_DIRECT)
            priority = value / distance;

        let importance: number | null = null;
        let occluded = false;

        if (reduce) {

            if (config.mapFeaturesReduceFactor >= 1) {

                reduce[1] = reduce[2];
                reduce[4] = distance;
            }

            importance = reduce[3]
                - config.mapFeaturesReduceFactor * Math.log(reduce[4]);

            occluded = config.mapFeaturesReduceFactor2 !== 0
                && rmap.occluded([x, y + stickShift, reduce, 0]);
        }

        const origin = this.part!.origin;

        this.collected!.push({
            key: job.placementKey,
            zIndex: job.zIndex,
            position: [x + origin[0], y + origin[1]],
            depth,
            rectangle: [left, top, right, bottom],
            stickShift,
            priority,
            importance,
            occluded,
        });

        return false;
    }

    private constructor(
        part: LabelPlacement.Part | null,
        placed: Map<string, LabelPlacement.Label> | null,
        readonly origin: readonly [number, number] = [0, 0],
    ) {

        this.part = part;
        this.placed_ = placed;
        this.sourceTiles_ = placed ? new Set() : null;
        this.collected = placed ? null : [];
    }

    private readonly draws_: (() => void)[] = [];
    private readonly placed_: Map<string, LabelPlacement.Label> | null;
    private readonly sourceTiles_: Set<string> | null;
}

/**
 * A label key of a tiled layer: layer id, then the tile's lod, x and y,
 * then the group and job indices and, under a visibility switch, two
 * more. The layer id is matched greedily, so it may contain slashes.
 */
const TileKey = /^(.*)\/(\d+)-(\d+)-(\d+)\/\d+\/\d+(?:\/\d+\/\d+)?$/;

/** The `scr-count7` reduce mode in slot 0 of a job's reduce record. */
const ScrCount7 = 10;

/** Whether the job is placed under the label budget. */
const budgeted = (job: LabelPlacement.Job): boolean =>
    !!job.reduce && job.reduce[0] === ScrCount7;

namespace LabelPlacement {

    /**
     * Where the part the canvas shows lies in the whole image, in CSS
     * pixels, x to the right and y down.
     */
    export type Part = {

        /** Width and height of the whole image. */
        imageSize: readonly [number, number];

        /** The part's top left corner in the image. */
        origin: readonly [number, number];
    };

    /**
     * A collected label. Plain data, so the caller can carry it between
     * the maps that render the parts.
     */
    export type Label = {

        key: string;

        /** The draw level; levels place independently. */
        zIndex: number;

        /** The anchor in CSS pixels of the whole image, y down. */
        position: [number, number];

        /** Anchor depth, shared by crops of the same image projection. */
        depth: number;

        /** The overlap rectangle about the anchor: left, top, right,
         * bottom. */
        rectangle: [number, number, number, number];

        stickShift: number;

        /** The rank among overlapping labels without a budget. */
        priority: number;

        /** The rank under the label budget; null without one. */
        importance: number | null;

        /** Whether terrain hides the anchor. */
        occluded: boolean;
    };

    /** What placement reads of a legacy label or icon job. */
    export type Job = {
        type: number;
        placementKey: string;
        zIndex: number;
        center2: ArrayLike<number>;
        noOverlap: [number, number, number, number, number | null, number];
        reduce: number[] | null;
    };
}

export default LabelPlacement;
